import React, { useState } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageBanner } from '../../components/PageBanner/PageBanner';
import { PageNavigation, Tab } from '../../components/PageNavigation/PageNavigation';
import { MultiStepForm, Step } from '../../components/MultiStepForm/MultiStepForm';
import { UserInfoStep } from '../../components/MultiStepForm/steps/UserInfoStep';
import { DateTimeStep } from '../../components/MultiStepForm/steps/DateTimeStep';
import { ConfirmationStep } from '../../components/MultiStepForm/steps/ConfirmationStep';
import { AdditionalInfoStep } from '../../components/MultiStepForm/steps/AdditionalInfoStep';
import { CompletedStep } from '../../components/MultiStepForm/steps/CompletedStep';
import { BookingConfirmation } from '../../components/BookingConfirmation/BookingConfirmation';
import formIconCeu from '../../assets/icons/form-icon-ceu.svg';
import formIconCalendar from '../../assets/icons/form-icon-calendar.svg';
import formIconInfo from '../../assets/icons/form-icon-info.svg';
import formIconConfirmNotReached from '../../assets/icons/form-icon-confirm-not-reached.svg';
import formIconConfirmReached from '../../assets/icons/form-icon-confirm-reached.svg';
import formIconNotReached from '../../assets/icons/form-icon-step-not-reached-yet.svg';
import { CityFilter } from '../../components/CityFilter/CityFilter';
import ShowroomTabs, { ShowroomTab } from '../../components/ShowroomTabs/ShowroomTabs';
import { useAppointments } from '../../hooks/useAppointments';
import { adaptAppointmentToCardEvent, Appointment } from '../../adapters/appointmentAdapter';
import './AppointmentPage.scss';
import { EventCard } from '@/components/EventCard/EventCard';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAppointmentBooking } from '../../hooks/useAppointmentBooking';

// Feature flag: Set to true to show appointments, false to show "Coming Soon"
const SHOW_APPOINTMENTS = import.meta.env.VITE_SHOW_APPOINTMENTS === "true";

const AppointmentPage: React.FC = () => {
  const { t } = useTranslation('appointments');
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState('Montreal');
  const [searchParams] = useSearchParams();
  
  const { data: userData, isLoading: isUserLoading } = useUserProfile();
  const [selectedShowroom, setSelectedShowroom] = useState<ShowroomTab>('all');
  const [activeTab, setActiveTab] = useState('booking');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  
  // Check for testingAppointments query parameter to bypass environment variable
  const isTestingMode = searchParams.get('testingAppointments') === 'true';
  const shouldShowAppointments = SHOW_APPOINTMENTS || isTestingMode;
  
  // Only fetch appointments if the feature is enabled or testing mode is active
  const { data: appointments, isLoading: isAppointmentsLoading } = shouldShowAppointments ? useAppointments(selectedCity) : { data: [], isLoading: false };
  const { bookAppointmentAsync, isAppointmentBooked, isPending: isBookingPending } = useAppointmentBooking();

  // useEffect hooks after all state declarations
  if (isUserLoading || isAppointmentsLoading) {
    return <Loader variant="fullscreen" />;
  }

  // Helper function to deduplicate appointments
  const deduplicateAppointments = (appointments: Appointment[] | undefined): Appointment[] => {
    if (!appointments) return [];
    
    return appointments.reduce((acc, appointment) => {
      // Create a unique key combining appointmentId, type, and territory
      const uniqueKey = `${appointment.appointmentId}-${appointment.appointmentType}-${appointment.location.territoryId}`;
      
      // Only add if we haven't seen this exact combination before
      if (!acc.some(a => 
        `${a.appointmentId}-${a.appointmentType}-${a.location.territoryId}` === uniqueKey
      )) {
        acc.push(appointment);
      }
      return acc;
    }, [] as Appointment[]);
  };

  // Deduplicate appointments once for use in counts and display
  const uniqueAppointments = deduplicateAppointments(appointments);

  const handleStepComplete = (stepData: Record<string, any>) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  };

  const eventsTabs: Tab[] = [
    { id: 'events', label: t('navigation.events') },
    { id: 'booking', label: t('navigation.booking') }
  ];

  const handleTabChange = (tabId: string) => {
    if (tabId === 'events') {
      navigate('/events');
    } else {
      setActiveTab(tabId);
    }
  };

  const handleCloseForm = () => {
    setShowBookingForm(false);
  };

  const handleFormComplete = async (formData: Record<string, any>): Promise<void> => {
    // Get appointment name to identify the type (e.g., "Product Consultation", "Private Introduction Class")
    const appointmentName = selectedAppointment?.name || '';
    const appointmentNameLower = appointmentName.toLowerCase();
    
    // Determine appointment type for conditional logic
    const isProductConsultation = appointmentNameLower.includes('product consultation');
    const isPrivateIntroClass = appointmentNameLower.includes('private introduction');
    
    // Ensure we have a valid appointmentId
    if (!selectedAppointment?.appointmentId) {
      console.error('Missing appointmentId in selectedAppointment:', selectedAppointment);
      throw new Error('Missing required appointment information');
    }

    // Ensure we have the correct workTypeGroupId from the selected time slot
    if (!formData.workTypeGroupId) {
      console.error('Missing workTypeGroupId in form data. This should be set when selecting a time slot.');
      throw new Error('Missing required workTypeGroupId. Please select a time slot again.');
    }

    // Base booking data with all required fields
    const isClientForm = isProductConsultation || isPrivateIntroClass;

    const bookingData = {
      // For client forms, use client fields and keep original contact info in firstname/lastname
      ...(isClientForm ? {
        firstname: formData.firstName || '',
        lastname: formData.lastName || '',
        email: formData.email || '',
        phone: formData.phone || '',
        clientFirstName: formData.clientFirstName || '',
        clientLastName: formData.clientLastName || '',
        clientPhoneNumber: formData.clientPhone || '',
        clientEmail: formData.clientEmail || '',
        willBePresentWithClient: formData.willAttend === 'yes' ? 'Yes' : 'No'
      } : {
        firstname: formData.firstName || '',
        lastname: formData.lastName || '',
        email: formData.email || '',
        phone: formData.phone || ''
      }),
      language: 'English',
      experienceCenter: selectedCity === 'Montreal' && selectedShowroom === 'RoyalMount Showroom' ? 
        'Montreal - RoyalMount' : selectedCity,
      appointmentType: appointmentName,
      addressStreet: formData.streetAddress || '',
      addressPostalCode: formData.postalCode || '',
      addressCity: formData.city || selectedCity || '',
      addressState: formData.province || '',
      addressCountry: 'Canada',
      appointmentId: selectedAppointment.appointmentId,
      startTime: formData.startTime,
      endTime: formData.endTime,
      serviceTerritoryId: formData.serviceTerritoryId,
      resourceIds: formData.resourceIds,
      workTypeGroupId: formData.workTypeGroupId,
      ...(appointmentNameLower.includes('boardroom') ? {
        meetingDetails: formData.meetingDetails || '',
        audioVideoRequirements: formData.audioVideoRequirements || '',
        cateringRequirements: formData.cateringRequirements || '',
        tradeProfessionals: formData.tradeProfessionals || '',
        additionalComments: formData.additionalComments || ''
      } : isProductConsultation ? {
        // Product Consultation specific fields - no guestName, no tradeProfessionals, no visitedShowroom
        foodDietaryRestriction: formData.foodAllergies || '',
        productsInterest: formData.productsInterested || '',
        applianceRetailer: formData.retailer || '',
        additionalComments: formData.additionalComments || ''
      } : {
        // Other appointment types (private-intro-class, private-ownership, etc.)
        foodDietaryRestriction: formData.foodAllergies || '',
        ...(formData.guestName ? { guestName: formData.guestName } : {}),
        productsInterest: formData.productsInterested || '',
        applianceRetailer: formData.retailer || '',
        ...(formData.tradeProfessionals ? { tradeProfessionals: formData.tradeProfessionals } : {}),
        additionalComments: formData.additionalComments || '',
        ...(formData.visitedShowroom ? { visitedShowroom: formData.visitedShowroom === 'yes' ? 'Yes' : 'No' } : {})
      })
    };
            
    try {
      // Submit booking to API
      await bookAppointmentAsync(bookingData, {
        onSuccess: () => {
          setShowBookingForm(false);
          setShowConfirmation(true);
        }
      });
    } catch (error) {
      console.error('Error booking appointment:', error);
      throw error; // Re-throw to let MultiStepForm handle the error state
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Helper to determine if additional info step should be shown
  const shouldShowAdditionalInfo = (formType: string | undefined): boolean => {
    if (!formType) return false;
    // Exclude event types, special-event, and unknown-appointment
    return !formType.startsWith('event-') && formType !== 'special-event' && formType !== 'unknown-appointment';
  };

  // Helper to get appointment title with fallback
  const getAppointmentTitle = (appointmentName: string | undefined): string => {
    if (!appointmentName) return '';
    
    // Convert name to camelCase for translation key
    const translationKey = `appointmentTitles.${appointmentName.split(' ').map((word: string, index: number) => 
      index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)
    ).join('')}`;
    
    // Try to get translation
    const translated = t(translationKey);
    
    // If translation key is returned as-is (not found), use the original name
    if (translated === translationKey || translated.startsWith('appointmentTitles.')) {
      return appointmentName.toUpperCase();
    }
    
    return translated.toUpperCase();
  };

  const bookingSteps: Step[] = [
    {
      id: 'event',
      icon: { src: formIconCeu, alt: 'Event Icon' },
      title: getAppointmentTitle(selectedAppointment?.name),
      Component: <CompletedStep onNext={() => {}} />
    },
    {
      id: 'datetime',
      icon: { src: formIconCalendar, alt: 'Calendar Icon' },
      title: t('form.steps.dateTime'),
      Component: <DateTimeStep 
        onNext={handleStepComplete} 
        initialData={formData} 
        appointmentId={selectedAppointment?.appointmentId}
        showroom={selectedAppointment?.showroom || selectedCity}
      />
    },
    {
      id: 'info',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconInfo : formIconNotReached,
        alt: 'Information Icon'
      },
      title: t('form.steps.yourInformation'),
      Component: <UserInfoStep
        onNext={handleStepComplete}
        initialData={formData}
        formType={(selectedAppointment && adaptAppointmentToCardEvent(selectedAppointment).formType) || 'product-consultation'}
      />
    },
    // Conditionally include Additional Info step
    ...(shouldShowAdditionalInfo(selectedAppointment ? adaptAppointmentToCardEvent(selectedAppointment).formType : undefined) ? [{
      id: 'additionalInfo',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconInfo : formIconNotReached,
        alt: 'Additional Info Icon'
      },
      title: t('form.steps.additionalInfo'),
      Component: <AdditionalInfoStep 
        onNext={handleStepComplete} 
        initialData={formData} 
        formType={(selectedAppointment && adaptAppointmentToCardEvent(selectedAppointment).formType) || 'product-consultation'}
      />
    }] : []),
    {
      id: 'confirm',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconConfirmReached : formIconConfirmNotReached,
        alt: 'Confirmation Icon'
      },
      title: t('form.steps.reviewAndConfirm'),
      Component: <ConfirmationStep 
        eventData={{
          ...(selectedAppointment ? adaptAppointmentToCardEvent(selectedAppointment) : {
            id: '',
            remoteObjectId: '',
            title: '',
            type: 'In-Person',
            duration: '',
            description: '',
            date: '',
            time: '',
            location: '',
            city: 'Montreal',
            showroom: 'Montreal Live Kitchen',
            imageUrl: '',
            imageEventType: 'product-consultation'
          }),
          // Include date and time from formData to display in confirmation step
          date: formData.date || '',
          time: formData.time || ''
        }} 
        initialData={formData} 
        onNext={handleStepComplete} 
        variant="appointment"
      />
    }
  ];

  return (
    <div className="appointment-page">
      <PageBanner
        title={t('banner.title')}
        subtitle={t('banner.subtitle')}
        userName={userData?.firstName || ''}
        showTagSaleButton={false}
        className="appointment-page__banner"
        userData={userData!}
      />
      <div className="appointment-page__content">
        <PageNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={eventsTabs}
        />
        {shouldShowAppointments ? (
        <div className="appointment-page__inner-content">
          <div className="appointment-page__appointments-content">
            <div className="filters-container">
              <CityFilter
                cities={['Toronto', 'Montreal']}
                selectedCity={selectedCity}
                onCityChange={(city) => {
                  setSelectedCity(city);
                  setSelectedShowroom('all');
                }}
              />
              {selectedCity === 'Montreal' && (
                <ShowroomTabs
                  selectedTab={selectedShowroom}
                  onTabChange={setSelectedShowroom}
                  tabs={[
                    { id: 'all', label: t('showroom.tabs.all'), count: uniqueAppointments.filter(a => a.city === selectedCity).length },
                    { 
                      id: 'Montreal Showroom', 
                      label: t('showroom.montrealShowroom'), 
                      count: uniqueAppointments.filter(a => {
                        if (a.city !== selectedCity) return false;
                        const showroomUpper = a.showroom?.toUpperCase() || '';
                        return showroomUpper.includes('MONTREAL') && !showroomUpper.includes('ROYALMOUNT');
                      }).length
                    },
                    { 
                      id: 'RoyalMount Showroom', 
                      label: t('showroom.royalmountShowroom'), 
                      count: uniqueAppointments.filter(a => {
                        if (a.city !== selectedCity) return false;
                        return a.showroom?.toUpperCase().includes('ROYALMOUNT');
                      }).length
                    }
                  ]}
                />
              )}
            </div>
            <div className="appointment-page__appointments-grid">
              {(() => {
                const filteredAppointments = uniqueAppointments.filter(appointment => {
                  // First filter by city
                  if (appointment.city !== selectedCity) return false;
                  
                  // Then filter by showroom if needed
                  if (selectedShowroom === 'all') return true;
                  
                  switch (selectedShowroom) {
                    case 'RoyalMount Showroom':
                      // Check for ROYALMOUNT in uppercase or mixed case
                      return appointment.showroom?.toUpperCase().includes('ROYALMOUNT');
                    case 'Montreal Showroom':
                      // Check for Montreal showrooms (but not ROYALMOUNT)
                      const showroomUpper = appointment.showroom?.toUpperCase() || '';
                      return showroomUpper.includes('MONTREAL') && !showroomUpper.includes('ROYALMOUNT');
                    default:
                      return false;
                  }
                });

                return filteredAppointments.length === 0 ? (
                  <div className="appointment-page__no-appointments">
                    <p>{t('noAppointments')}</p>
                  </div>
                ) : (
                  filteredAppointments.map((appointment) => {
                    // Create unique key for React
                    const uniqueKey = `${appointment.appointmentId}-${appointment.appointmentType}-${appointment.location.territoryId}`;
                    
                    return (
                      <EventCard
                        key={uniqueKey}
                        event={adaptAppointmentToCardEvent(appointment)}
                        formVariant="appointment"
                        isBooked={isAppointmentBooked(appointment.appointmentId)}
                        onClick={() => {
                          if (!isAppointmentBooked(appointment.appointmentId)) {
                            setSelectedAppointment(appointment);
                            setShowBookingForm(true);
                          }
                        }}
                      />
                    );
                  })
                );
              })()}
            </div>
          </div>
        </div>
      ) : (
            <h1>{t('comingSoon')}</h1>
          )}
      </div>

      {showBookingForm && (
        <MultiStepForm
          steps={bookingSteps}
          initialStep={0}
          onClose={handleCloseForm}
          onComplete={handleFormComplete}
          variant="appointment"
          isSubmitting={isBookingPending}
          appointmentType={selectedAppointment?.name}
        />
      )}

      {showConfirmation && (
        <BookingConfirmation onClose={handleCloseConfirmation} />
      )}
      {/* {isBookingPending && (
        <div className="appointment-page__booking-loading">
          <Loader variant="overlay" />
          <p>{t('booking.loading')}</p>
        </div>
      )} */}
    </div>
  );
};

export default AppointmentPage;
